import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Divider,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Switch,
  CircularProgress,
  Alert,
  Snackbar,
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  Visibility,
  VisibilityOff,
  Comment,
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { CompassNote, CreateNoteRequest, UpdateNoteRequest, Compass } from '../types';
import { apiService } from '../services/api';
import { formatDistanceToNow } from 'date-fns';
import { useAuth } from '../contexts/AuthContext';

const CompassNotesPage: React.FC = () => {
  const { compassId } = useParams<{ compassId: string }>();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [compass, setCompass] = useState<Compass | null>(null);
  const [notes, setNotes] = useState<CompassNote[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [noteDialogOpen, setNoteDialogOpen] = useState(false);
  const [editingNote, setEditingNote] = useState<CompassNote | null>(null);
  const [noteContent, setNoteContent] = useState('');
  const [isPrivate, setIsPrivate] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success'
  });

  useEffect(() => {
    if (compassId) {
      loadCompassAndNotes();
    }
  }, [compassId]);

  const loadCompassAndNotes = async () => {
    if (!compassId) return;
    
    try {
      setLoading(true);
      const [compassData, notesData] = await Promise.all([
        apiService.getCompass(compassId),
        apiService.getCompassNotes(compassId)
      ]);
      setCompass(compassData);
      setNotes(notesData);
    } catch (err) {
      setError('Failed to load compass notes');
      console.error('Error loading compass notes:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleAddNote = () => {
    setEditingNote(null);
    setNoteContent('');
    setIsPrivate(false);
    setNoteDialogOpen(true);
  };

  const handleEditNote = (note: CompassNote) => {
    setEditingNote(note);
    setNoteContent(note.content);
    setIsPrivate(!note.visible_to_owner);
    setNoteDialogOpen(true);
  };

  const handleSaveNote = async () => {
    if (!compassId || !noteContent.trim()) return;

    try {
      setSubmitting(true);
      
      if (editingNote) {
        // Update existing note
        const updateData: UpdateNoteRequest = {
          content: noteContent.trim(),
          visible_to_owner: !isPrivate
        };
        const updatedNote = await apiService.updateNote(editingNote.id, updateData);
        setNotes(prev => prev.map(note => 
          note.id === editingNote.id ? updatedNote : note
        ));
        setSnackbar({
          open: true,
          message: 'Note updated successfully',
          severity: 'success'
        });
      } else {
        // Create new note
        const createData: CreateNoteRequest = {
          content: noteContent.trim(),
          visible_to_owner: !isPrivate
        };
        const newNote = await apiService.addCompassNote(compassId, createData);
        setNotes(prev => [newNote, ...prev]);
        setSnackbar({
          open: true,
          message: 'Note added successfully',
          severity: 'success'
        });
      }
      
      setNoteDialogOpen(false);
      setNoteContent('');
      setIsPrivate(false);
      setEditingNote(null);
    } catch (err) {
      setSnackbar({
        open: true,
        message: 'Failed to save note',
        severity: 'error'
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteNote = async (noteId: string) => {
    if (!window.confirm('Are you sure you want to delete this note?')) return;

    try {
      await apiService.deleteNote(noteId);
      setNotes(prev => prev.filter(note => note.id !== noteId));
      setSnackbar({
        open: true,
        message: 'Note deleted successfully',
        severity: 'success'
      });
    } catch (err) {
      setSnackbar({
        open: true,
        message: 'Failed to delete note',
        severity: 'error'
      });
    }
  };

  const canEditNote = (note: CompassNote) => {
    return user && note.user_id === user.id;
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  if (!compass) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        Compass not found
      </Alert>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" component="h1" fontWeight="bold">
            Notes
          </Typography>
          <Typography variant="h6" color="text.secondary" sx={{ mt: 1 }}>
            {compass.title || 'Untitled Compass'}
          </Typography>
        </Box>
        
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={handleAddNote}
        >
          Add Note
        </Button>
      </Box>

      {/* Compass Preview */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Compass Overview
          </Typography>
          <Box display="flex" gap={2} flexWrap="wrap" mb={2}>
            <Chip 
              label={compass.status} 
              color={compass.status === 'submitted' ? 'success' : 'warning'}
              size="small"
            />
            <Typography variant="caption" color="text.secondary">
              Created {formatDistanceToNow(new Date(compass.created_at), { addSuffix: true })}
            </Typography>
          </Box>
          
          {compass.what_happened && (
            <Box mb={2}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                What happened:
              </Typography>
              <Typography variant="body2">
                {compass.what_happened.length > 200 
                  ? `${compass.what_happened.substring(0, 200)}...` 
                  : compass.what_happened}
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Notes List */}
      <Card>
        <CardContent>
          <Box display="flex" alignItems="center" gap={2} mb={2}>
            <Comment color="action" />
            <Typography variant="h6">
              Notes ({notes.length})
            </Typography>
          </Box>

          {notes.length === 0 ? (
            <Box display="flex" flexDirection="column" alignItems="center" py={4}>
              <Comment sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No notes yet
              </Typography>
              <Typography variant="body2" color="text.secondary" textAlign="center">
                Add notes to provide feedback, insights, or additional context to this compass.
              </Typography>
            </Box>
          ) : (
            <List>
              {notes.map((note, index) => (
                <React.Fragment key={note.id}>
                  <ListItem 
                    alignItems="flex-start"
                    sx={{ 
                      px: 0,
                      py: 2,
                      bgcolor: note.visible_to_owner ? 'transparent' : 'action.hover',
                      borderRadius: 1,
                      mb: 1
                    }}
                  >
                    <Box width="100%">
                      <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Typography variant="subtitle2" fontWeight="bold">
                            {note.user?.name || 'Unknown User'}
                          </Typography>
                          {!note.visible_to_owner && (
                            <Chip 
                              label="Private" 
                              size="small" 
                              color="secondary"
                              icon={<VisibilityOff />}
                            />
                          )}
                        </Box>
                        
                        {canEditNote(note) && (
                          <Box display="flex" gap={1}>
                            <IconButton 
                              size="small" 
                              onClick={() => handleEditNote(note)}
                              title="Edit note"
                            >
                              <Edit fontSize="small" />
                            </IconButton>
                            <IconButton 
                              size="small" 
                              onClick={() => handleDeleteNote(note.id)}
                              title="Delete note"
                              color="error"
                            >
                              <Delete fontSize="small" />
                            </IconButton>
                          </Box>
                        )}
                      </Box>
                      
                      <Typography variant="body2" sx={{ mb: 1, whiteSpace: 'pre-wrap' }}>
                        {note.content}
                      </Typography>
                      
                      <Typography variant="caption" color="text.secondary">
                        {formatDistanceToNow(new Date(note.created_at), { addSuffix: true })}
                        {note.updated_at !== note.created_at && (
                          <> • Edited {formatDistanceToNow(new Date(note.updated_at), { addSuffix: true })}</>
                        )}
                      </Typography>
                    </Box>
                  </ListItem>
                  
                  {index < notes.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Note Dialog */}
      <Dialog 
        open={noteDialogOpen} 
        onClose={() => setNoteDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {editingNote ? 'Edit Note' : 'Add Note'}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            multiline
            rows={4}
            fullWidth
            label="Note content"
            value={noteContent}
            onChange={(e) => setNoteContent(e.target.value)}
            sx={{ mt: 1, mb: 2 }}
            placeholder="Add your thoughts, feedback, or insights about this compass..."
          />
          
          <FormControlLabel
            control={
              <Switch
                checked={isPrivate}
                onChange={(e) => setIsPrivate(e.target.checked)}
              />
            }
            label={
              <Box>
                <Typography variant="body2">
                  Private note
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {isPrivate 
                    ? 'Only you can see this note' 
                    : 'The compass owner can see this note'}
                </Typography>
              </Box>
            }
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNoteDialogOpen(false)}>
            Cancel
          </Button>
          <Button 
            onClick={handleSaveNote}
            variant="contained"
            disabled={!noteContent.trim() || submitting}
          >
            {submitting ? <CircularProgress size={20} /> : (editingNote ? 'Update' : 'Add Note')}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
      >
        <Alert
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CompassNotesPage;
